import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Users, Search, UserCheck, UserPlus, Trash2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COUNT_LABELS = [
  ['orders', 'Orders'],
  ['payment_transactions', 'Payment txns'],
  ['cart_items', 'Cart items'],
  ['wishlist', 'Wishlist'],
  ['addresses', 'Addresses'],
  ['reviews', 'Reviews'],
  ['subscriptions', 'Subscriptions'],
  ['coupon_usage', 'Coupon usage'],
  ['sessions', 'Sessions'],
];

const AdminUsers = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ customers: [], visitors: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // { user_id, email, name, role, counts }
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/users`, { withCredentials: true });
      setData(data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filterRows = (rows) => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q));
  };

  const customers = useMemo(() => filterRows(data.customers || []), [data, search]);
  const visitors = useMemo(() => filterRows(data.visitors || []), [data, search]);

  const openDeleteConfirm = async (u) => {
    setConfirmOpen(true);
    setConfirmLoading(true);
    setConfirmTarget({ user_id: u.user_id, email: u.email, name: u.name, role: u.role, counts: null });
    try {
      const { data } = await axios.get(`${API}/api/admin/users/${u.user_id}/counts`, { withCredentials: true });
      setConfirmTarget(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load user details');
      setConfirmOpen(false);
      setConfirmTarget(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  const performDelete = async () => {
    if (!confirmTarget?.user_id) return;
    setDeleting(true);
    try {
      const { data: res } = await axios.delete(`${API}/api/admin/users/${confirmTarget.user_id}`, { withCredentials: true });
      const c = res.cascade_deleted || {};
      const removedCount = Object.values(c).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Purged ${confirmTarget.email} — ${removedCount} related record${removedCount === 1 ? '' : 's'} also removed.`);
      setConfirmOpen(false);
      setConfirmTarget(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => navigate('/admin')} data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <h1 className="text-3xl font-black font-heading flex items-center gap-2" data-testid="admin-users-heading">
              <Users className="h-7 w-7 text-primary" /> Users
            </h1>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="users-search-input"
            />
          </div>
        </div>

        {/* Customers section */}
        <section className="bg-white rounded-xl border border-[#E0E0E0] mb-8 overflow-hidden" data-testid="customers-section">
          <div className="px-5 py-4 border-b border-[#E0E0E0] flex items-center justify-between bg-[#F8FAF8]">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary" />
              <h2 className="font-bold font-heading text-lg">Customers</h2>
              <span className="text-xs text-muted-foreground">— registered users with at least one purchase</span>
            </div>
            <span className="text-sm font-bold bg-primary/10 text-primary rounded-full px-3 py-1" data-testid="customers-count">
              {data.totals?.customers || 0}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E0E0E0]">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 text-right">Orders</th>
                  <th className="px-4 py-3 text-right">Total Spent</th>
                  <th className="px-4 py-3">Last Order</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No customers match your search.</td></tr>
                ) : customers.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`customer-row-${u.user_id}`}>
                    <td className="px-4 py-3 font-medium">{u.name || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3 text-right font-bold">{u.order_count}</td>
                    <td className="px-4 py-3 text-right font-bold text-primary">{'\u20B9'}{(u.total_spent || 0).toFixed(0)}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(u.last_order_at)}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteConfirm(u)}
                        data-testid={`delete-user-${u.user_id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Visitors section */}
        <section className="bg-white rounded-xl border border-[#E0E0E0] overflow-hidden" data-testid="visitors-section">
          <div className="px-5 py-4 border-b border-[#E0E0E0] flex items-center justify-between bg-[#FFF7EE]">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-accent" />
              <h2 className="font-bold font-heading text-lg">Visitors</h2>
              <span className="text-xs text-muted-foreground">— registered but have not purchased yet</span>
            </div>
            <span className="text-sm font-bold bg-accent/10 text-accent rounded-full px-3 py-1" data-testid="visitors-count">
              {data.totals?.visitors || 0}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E0E0E0]">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visitors.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No visitors match your search.</td></tr>
                ) : visitors.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`visitor-row-${u.user_id}`}>
                    <td className="px-4 py-3 font-medium">{u.name || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-blue-50 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteConfirm(u)}
                        data-testid={`delete-user-${u.user_id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => { if (!open && !deleting) { setConfirmOpen(false); setConfirmTarget(null); } }}>
        <AlertDialogContent data-testid="delete-user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Permanently delete this user?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {confirmTarget ? (
                  <div className="rounded-lg border border-red-100 bg-red-50/40 p-3">
                    <div className="font-semibold text-foreground">{confirmTarget.name || '—'}</div>
                    <div className="text-muted-foreground break-all">{confirmTarget.email}</div>
                    {confirmTarget.role === 'admin' && (
                      <div className="mt-2 text-[11px] uppercase font-bold tracking-wide inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-800">admin</div>
                    )}
                  </div>
                ) : null}

                {confirmLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading related-data counts…
                  </div>
                ) : confirmTarget?.counts ? (
                  <div>
                    <div className="text-foreground font-medium mb-1">The following will also be deleted:</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1" data-testid="delete-user-counts-grid">
                      {COUNT_LABELS.map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between border-b border-dashed border-[#E5E5E5] py-1">
                          <span className="text-muted-foreground">{label}</span>
                          <span className={`font-bold tabular-nums ${confirmTarget.counts[key] ? 'text-red-700' : 'text-muted-foreground'}`} data-testid={`count-${key}`}>
                            {confirmTarget.counts[key] || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-red-700 font-medium">
                      This action is permanent. There is no undo.
                    </div>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); performDelete(); }}
              disabled={deleting || confirmLoading}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-user-confirm"
            >
              {deleting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting…</>) : 'Yes, delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsers;
